'use client';

import React, { useMemo, useState } from 'react';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { tagsForTarget } from './ontology/tags';
import type { FeedbackSubmission } from './ontology/types';
import { submitFeedback } from './submit';

/** Describes the dialog state and captured snapshot consumed by FeedbackForm. */
interface FeedbackFormProps {
  isOpen: boolean;
  snapshot: Omit<FeedbackSubmission, 'tags' | 'unselected_tags' | 'message'>;
  onClose: () => void;
}

/** Collects feedback tags and details, then submits a normalized feedback record. */
export const FeedbackForm: React.FC<FeedbackFormProps> = ({ isOpen, snapshot, onClose }) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');

  const offeredTags: string[] = useMemo(() => {
    return tagsForTarget(snapshot.target);
  }, [snapshot.target]);

  const normalizedMessage: string = message.trim();
  const canSubmit: boolean = selectedTags.length >= 1 || normalizedMessage !== '';

  /** Toggles whether a tag is included in the pending feedback submission. */
  const toggleTag = (tag: string) => {
    setSelectedTags((currentTags) => {
      return currentTags.includes(tag)
        ? currentTags.filter((currentTag) => currentTag !== tag)
        : [...currentTags, tag];
    });
  };

  /** Submits the current feedback record when the user has provided input. */
  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }

    const selectedTagSet = new Set(selectedTags);
    const record: FeedbackSubmission = {
      ...snapshot,
      tags: selectedTags,
      unselected_tags: offeredTags.filter((tag) => !selectedTagSet.has(tag)),
      message: normalizedMessage,
    };

    submitFeedback(record);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Give feedback</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Select any tags that fit, then add optional details.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {offeredTags.map((tag: string) => {
                const isSelected: boolean = selectedTags.includes(tag);

                return (
                  <Chip
                    key={tag}
                    label={tag}
                    clickable
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => toggleTag(tag)}
                    sx={{
                      borderColor: isSelected
                        ? 'var(--mui-palette-primary-main)'
                        : 'var(--mui-palette-divider)',
                      color: isSelected
                        ? 'var(--mui-palette-primary-contrastText)'
                        : 'var(--mui-palette-text-primary)',
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
          <TextField
            label="Tell us more"
            value={message}
            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setMessage(event.target.value)
            }
            multiline
            minRows={4}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit} variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
